import {forwardRef,useImperativeHandle,useEffect,useState,useCallback,useMemo} from "react";
import {useNavigate} from 'react-router-dom';
import {useSnackbar} from '../SnackbarContext.js';
import {useLoading} from '../LoadingContext';
import {consoleLog,getLimitFromParams,stripPagingParams,patchRowLocally} from '../../utilities/Utilities';
import {getObjectRecords,deleteObjectRecordsByIdList,updateObjectRecord,getObjectRecordCount,getAttachment} from '../../integration/ObjectManager.js';
import COMPONENT_MAP from '../Repository/Components.js';
import {useConfirm} from '../ConfirmContext.js';
import {useApplication} from '../ApplicationContext.js';
let rowId=null;
let _parent={};
const ListTab=forwardRef(({parentId,parent,refreshLineAfterFieldUpdate=false,refreshParentAfterFieldUpdate=false,refreshParentAfterRecordDelete=false,refreshToken,application,userData,entityType,entityParams,file,list_name,focusOnSearchInput=false,initialState,height,navigateOnNew,canCreateNew,filterComponent,checkboxSelection,disableRowSelectionOnClick,disableSelectionOnClick,button_actions,pageCallback,popupComponent:PopupComponent,popupComponentQuery=[],parentContext,executeQuery={current:true},getRowClassName=null,dataGridSx=null,defaultQuickSearchColumns,fromPopup=false},ref)=>{
    const [loading,setLoading]=useState(true);
    const [records,setRecords]=useState([]);
    const [entityRules,setEntityRules]=useState({});
    const navigate=useNavigate();
    const {globalData,updateGlobalData}=useApplication();
    const popupApplication=useMemo(()=>({
        globalData,
        updateGlobalData,
        getLocale:(key,options)=>globalData?.getLocale(key,options)
    }),[globalData,updateGlobalData]);
    const {showSnackbar}=useSnackbar();
    const {showLoading,updateProgress,hideLoading}=useLoading();
    const entityParamsKey=useMemo(()=>(entityParams??[]).join("|"),[entityParams]);
    const [selection,setSelection]=useState([]);
    const [openPopup,setOpenPopup]=useState(false);
    const {confirm}=useConfirm();

    const getRecords=useCallback(async (isSubscribed=true)=>{
        if (!isSubscribed) return;

        const limit=getLimitFromParams(entityParams);
        const isBatchMode=!!limit;

        if(!isBatchMode){
            const data=await getObjectRecords({entityType,entityParams});
            if (!isSubscribed) return;
            consoleLog({file:file,context:'getRecords - getObjectRecords',arg_name:'data',arg_value:data});
            if ('error' in data) {
                showSnackbar(data.error,'error');
                setRecords([]);
            } 
            else {
                setRecords(data.records||[]);
                setEntityRules(data.entity_rules);
            }
            setLoading(false);
            executeQuery.current=false;
            return;
        }
        showLoading(0);
        const baseParams=stripPagingParams(entityParams);
        const countData=await getObjectRecordCount({entityType,entityParams:baseParams});
        consoleLog({file:file,context:'getRecords - getObjectRecordCount',arg_name:'countData',arg_value:countData});
        if('error' in countData){
            showSnackbar(countData.error,'error');
            setLoading(false);
            return;
        }
        const total=countData.count||0;
        let offset=0;
        setRecords([]);
        let entityRulesLoaded=false;
        if(total===0){
            const data=await getObjectRecords({entityType,entityParams:baseParams});
            consoleLog({file:file,context:'getRecords - getObjectRecords',arg_name:'data',arg_value:data});
            if('error' in data){
                showSnackbar(data.error,'error');
            }
            if(!entityRulesLoaded&&data.entity_rules){
                setEntityRules(data.entity_rules);
                entityRulesLoaded=true;
            }
        }
        else {
            while(offset<total){
                const batchParams=[...baseParams,`_limit=${limit}`,`_offset=${offset}`];
                const data=await getObjectRecords({entityType,entityParams:batchParams});
                if (!isSubscribed) return;
                consoleLog({file:file,context:'getRecords - getObjectRecords',arg_name:'data',arg_value:data});
                if('error' in data){
                    showSnackbar(data.error,'error');
                    break;
                }
                setRecords(prev=>[...prev, ...(data.records||[])]);
                offset+=(data.records||[]).length;
                const percent=Math.min(100,Math.round((offset/total)*100));
                updateProgress(percent);
                if(!entityRulesLoaded&&data.entity_rules){
                    setEntityRules(data.entity_rules);
                    entityRulesLoaded=true;
                }
                await new Promise(resolve=>setTimeout(resolve,80));
            }
        }
        executeQuery.current=false;
        hideLoading();
        setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[parentId,entityParams]);

    useEffect(()=>{
        let isSubscribed=true;
        if(parentId){
            executeQuery.current=true;
            if(executeQuery.current){
                getRecords(isSubscribed);
                executeQuery.current=false;
            }
        }
        else{
            executeQuery.current=true;
            setLoading(false);
        }
        return()=>{
            isSubscribed=false;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[parentId,refreshToken,entityParamsKey]);

    const updateRecordField=async(entityId,entityData)=>{
        let data=await updateObjectRecord({
            entityType:entityType,
            entityId:entityId,
            entityData:entityData,
            getUpdatedRecord:true
        });
        consoleLog({file:file,context:'const',arg_name:'data',arg_value:data});
        //A backend business validation rejection comes back as header_validation, not error -
        //normalize it so the snackbar shows it and List.processRowUpdate reverts the edited cell
        if(data?.header_validation?.error){
            data={error:data.header_validation.message};
        }
        if('error' in data){
            showSnackbar(data.error, 'error');
        } 
        else{
            if(data?.operation_message){//saved, but the backend adjusted something the user must know (e.g. a rounded value)
                showSnackbar(data.operation_message,'warning');
            }
            else{
                showSnackbar(application.getLocale("RecordUpdatedWithSuccess"),'success');
            }
            if(refreshLineAfterFieldUpdate){
                patchRowLocally(entityId,data,setRecords);
            }
            if(refreshParentAfterFieldUpdate){
                pageCallback('refresh_parent');
            }
        }
        return data;
    };

    const handleClickEvent=async(args)=>{
        consoleLog({file:file,context:'handleClickEvent',arg_name:'event',arg_value:args});
        switch(args?.action){
            case "updateFields":
                return updateRecordField(args.entityId,args.entityData);
            case "new":
                if(navigateOnNew){
                    navigate(navigateOnNew.route,navigateOnNew.args);
                }
                else{
                    rowId=null;
                    _parent=JSON.parse(JSON.stringify(parentContext));
                    _parent.parent_field_value=parentId;
                    setOpenPopup(true);
                }
                break;
            case "refresh":
                setRecords([]);
                getRecords();
                break;
            case "openPopup":
                rowId=args?.id;
                _parent=JSON.parse(JSON.stringify(parentContext));
                _parent.parent_field_value=parentId;
                setOpenPopup(true);
                break;
            case "removeSelection":
                
                if(await confirm({message:selection.length>1?application.getLocale("ConfirmMultipleRecordDeletion").replace("{RECORDS}",selection.length):application.getLocale("ConfirmRecordDeletion")})){
                    deleteRecords(selection);
                }
                break;
            case "invoke_method":
                pageCallback('invoke_method',args);
                break;
            case "download":
                downloadAttachment(args.id,args.filename);
                break;
            default:
                break;
        }
    }

    //Generic row-level "download" action for any Attachment-backed list (e.g. a column built via
    //handleClickEvent({action:'download',id,filename})) - fetches the file as a blob and triggers
    //a normal browser save, same trick used by ImageTab.js.
    const downloadAttachment=async(id,filename)=>{
        const data=await getAttachment(id);
        if(data.error){
            showSnackbar(data.error,'error');
            return;
        }
        const link=document.createElement('a');
        link.href=data.src;
        link.download=filename||'';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(data.src);
    }

    const deleteRecords=(idList)=>{
        consoleLog({file:file,context:'deleteRecord',arg_name:'idList',arg_value:idList});
        showLoading();
        //Forward the list's own _context (e.g. parent_name/parent_id) so models whose
        //can_delete_record rules depend on the parent record can resolve it - without this,
        //every context-dependent model rejects the delete regardless of permissions.
        const contextParam=(entityParams??[]).find((param)=>param.startsWith('_context='));
        const deleteParams=contextParam?['idList='+idList,contextParam]:['idList='+idList];
        deleteObjectRecordsByIdList({entityType:entityType,entityParams:deleteParams})
        .then((data)=>{
            consoleLog({file:file,context:'deleteRecords - deleteObjectRecordsByIdList',arg_name:'data',arg_value:data});
            if('error' in data){
                showSnackbar(data.error,'error');
            }
            else{
                showSnackbar(data.deleted>1?application.getLocale("RecordsRemovedSuccess"):application.getLocale("RecordRemovedSuccess"),'success');//update string
                setSelection(old=>[]);
                getRecords();
                if(refreshParentAfterRecordDelete){
                    pageCallback('refresh_parent');
                }
            }
            hideLoading();
        });
    };

    const handleSelection=(event)=>{
        const ids=Array.from(event.ids);
        consoleLog({file:file,context:'onRowSelectionModelChange',arg_name:'ids',arg_value:ids});
        setSelection(old=>ids);
    };

    const popupCallBack=(action,args)=>{
        if(action==='refreshParent'){
            handleClickEvent({action:'refresh'});
            if('close' in args){
                if(args.close){
                    setOpenPopup(false);
                }
            }
        }
        else if(action==='closePopup'){
            setOpenPopup(false);
        }
        else if(action==='invokeMethod'){
            setOpenPopup(false);
            pageCallback('invoke_method',args);
        }
    }

    useImperativeHandle(ref,()=>({
        handleClickEvent
    }));

    const renderList=useMemo(()=>{
        const Component=COMPONENT_MAP['List'];
        return Component?(
            <Component
                records={records}
                list_name={list_name}
                loading={loading}
                height={height}
                file={file}
                application={application}
                initialState={initialState}
                entityRules={entityRules}
                userData={userData}
                focusOnSearchInput={focusOnSearchInput}
                getRowClassName={getRowClassName}
                dataGridSx={dataGridSx}
                canCreateNew={canCreateNew}
                button_actions={button_actions}
                handleClickEvent={handleClickEvent}
                handleSelection={handleSelection}
                filterComponent={filterComponent}
                checkboxSelection={checkboxSelection??false}
                disableRowSelectionOnClick={disableRowSelectionOnClick??false}
                disableSelectionOnClick={disableSelectionOnClick??false}
                canRemoveSelection={selection.length>0}
                defaultQuickSearchColumns={defaultQuickSearchColumns}
            />
        ):null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[loading,records,entityRules,selection,application.globalData.dataGridParams[list_name]])

    return(
        <>
            {openPopup&&<PopupComponent popupCallBack={popupCallBack} query={popupComponentQuery} application={popupApplication} rowId={rowId} parent={_parent} userData={userData} fromPopup={fromPopup}></PopupComponent>}
            {renderList}
        </>
    );
});

export default ListTab;
